/**
 * Painel de Timeline de Eventos
 * Exibe eventos extraídos da transcrição em ordem cronológica
 */

import React, { useState, useEffect, useCallback, memo } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import TimelineIcon from '@mui/icons-material/Timeline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import GavelIcon from '@mui/icons-material/Gavel'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import VerifiedIcon from '@mui/icons-material/Verified'
import WarningIcon from '@mui/icons-material/Warning'
import HandshakeIcon from '@mui/icons-material/Handshake'
import StarIcon from '@mui/icons-material/Star'
import RefreshIcon from '@mui/icons-material/Refresh'
import FilterListIcon from '@mui/icons-material/FilterList'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import { EventTimeline, TimelineEvent, EventCategory } from '../../../types/transcription'
import { getEventTimeline, runOnDemandAnalysis } from '../../../services/api'
import AnalysisOutdatedBanner from './AnalysisOutdatedBanner'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'
import AnalysisSkippedCard from './AnalysisSkippedCard'
import AnalysisProcessingCard from './AnalysisProcessingCard'
import HybridAnalysisList from './HybridAnalysisList'
import { SpeakerMapping } from '../../../services/exportService'

type Props = {
  fileId: string
  onNavigateToSegment?: (segmentIndex: number) => void
  speakerMapping?: SpeakerMapping
  /** Status atual desta análise (do polling) */
  analysisTypeStatus?: string
  /** Callback quando análise on-demand é iniciada */
  onAnalysisStarted?: () => void
  /** Flag indicando que segmentos foram editados após esta análise */
  analysisOutdated?: boolean
}

const CATEGORY_CONFIG: Record<EventCategory, { 
  label: string
  icon: React.ElementType
  color: string
  bgColor: string 
}> = {
  action: {
    label: 'Ação',
    icon: PlayArrowIcon,
    color: '#1976d2',
    bgColor: '#e3f2fd'
  },
  statement: {
    label: 'Declaração',
    icon: RecordVoiceOverIcon,
    color: '#7b1fa2',
    bgColor: '#f3e5f5'
  },
  evidence: {
    label: 'Evidência',
    icon: VerifiedIcon,
    color: '#388e3c',
    bgColor: '#e8f5e9'
  },
  decision: {
    label: 'Decisão',
    icon: GavelIcon,
    color: '#f57c00',
    bgColor: '#fff3e0'
  },
  threat: {
    label: 'Ameaça/Risco',
    icon: WarningIcon,
    color: '#d32f2f',
    bgColor: '#ffebee'
  },
  agreement: {
    label: 'Acordo',
    icon: HandshakeIcon,
    color: '#00796b',
    bgColor: '#e0f2f1'
  }
}

const IMPORTANCE_CONFIG = {
  high: { label: 'Alta', color: '#d32f2f' },
  medium: { label: 'Média', color: '#f57c00' },
  low: { label: 'Baixa', color: '#757575' }
}

/**
 * Formata o tempo para exibição.
 * Aceita tanto segundos (número ou string numérica) quanto formato HH:MM:SS.
 */
function formatTime(time: string): string {
  if (!time) return '0:00'
  
  // Se já está no formato HH:MM:SS ou MM:SS, retorna diretamente (ou simplificado)
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      // HH:MM:SS - remover horas se for 00
      const hours = parseInt(parts[0], 10)
      const mins = parseInt(parts[1], 10)
      const secs = parseInt(parts[2], 10)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    // MM:SS - retorna como está
    return time
  }
  
  // Se for número de segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function EventCardBase({
  event,
  isLast,
  showConnector,
  onNavigateToSegment,
  style,
  compact = false,
  speakerMapping = {}
}: {
  event: TimelineEvent
  isLast: boolean
  showConnector: boolean
  onNavigateToSegment?: (segmentIndex: number) => void
  style?: React.CSSProperties
  compact?: boolean
  speakerMapping?: SpeakerMapping
}) {
  // Validar categoria e fornecer fallback
  const config = CATEGORY_CONFIG[event.category] || {
    label: 'Outro',
    icon: StarIcon,
    color: '#757575',
    bgColor: '#f5f5f5'
  }
  const Icon = config.icon
  const importanceConfig = IMPORTANCE_CONFIG[event.importance] || IMPORTANCE_CONFIG.medium

  // Mapear nomes dos participants
  const mappedParticipants = event.participants.map(p => speakerMapping[p] || p)

  // Limit participants on mobile
  const displayParticipants = compact ? mappedParticipants.slice(0, 2) : mappedParticipants
  const hiddenParticipantsCount = compact ? mappedParticipants.length - 2 : 0

  return (
    <div style={{ ...style, paddingRight: compact ? 4 : 8, paddingBottom: 4 }}>
      <Box sx={{ display: 'flex', gap: compact ? 1 : 2, height: 'calc(100% - 4px)' }}>
        {/* Timeline connector */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: compact ? 32 : 44,
              height: compact ? 32 : 44,
              borderRadius: '50%',
              bgcolor: config.bgColor,
              border: compact ? `2px solid ${config.color}` : `3px solid ${config.color}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              flexShrink: 0
            }}
          >
            <Icon sx={{ color: config.color, fontSize: compact ? 16 : 22 }} />
          </Box>
          {showConnector && !isLast && (
            <Box
              sx={{
                width: compact ? 2 : 3,
                flex: 1,
                bgcolor: '#e0e0e0',
                minHeight: compact ? 12 : 20
              }}
            />
          )}
        </Box>

        {/* Event content */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: compact ? 1.25 : 2,
            border: '1px solid #e0e0e0',
            borderLeft: compact ? `3px solid ${config.color}` : `4px solid ${config.color}`,
            overflow: 'hidden',
            '&:hover': {
              boxShadow: compact ? 1 : 2,
              borderColor: config.color
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: compact ? 0.5 : 1, gap: 0.5, flexWrap: compact ? 'wrap' : 'nowrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                sx={{
                  bgcolor: '#f5f5f5',
                  px: compact ? 0.75 : 1,
                  py: compact ? 0.25 : 0.5,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  fontSize: compact ? '0.6rem' : '0.75rem'
                }}
              >
                {formatTime(event.timestamp)}
              </Typography>
              <Chip
                label={config.label}
                size="small"
                sx={{
                  bgcolor: config.bgColor,
                  color: config.color,
                  fontWeight: 600,
                  fontSize: compact ? '0.55rem' : '0.7rem',
                  height: compact ? 18 : 24
                }}
              />
              {event.importance === 'high' && (
                <Tooltip title="Evento importante" disableHoverListener={compact}>
                  <StarIcon sx={{ color: '#ffc107', fontSize: compact ? 14 : 18 }} />
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.25 : 1, flexShrink: 0 }}>
              {!compact && (
                <Chip
                  label={importanceConfig.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.65rem',
                    color: importanceConfig.color,
                    borderColor: importanceConfig.color
                  }}
                />
              )}
              {onNavigateToSegment && (
                <Tooltip title="Ir para transcrição" disableHoverListener={compact}>
                  <IconButton
                    size="small"
                    onClick={() => onNavigateToSegment(event.segmentIndex)}
                    sx={{
                      color: config.color,
                      p: compact ? 0.25 : 0.5,
                      '&:hover': { bgcolor: config.bgColor }
                    }}
                  >
                    <ArrowForwardIcon sx={{ fontSize: compact ? 16 : 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: compact ? 0.25 : 0.5,
              fontSize: compact ? '0.8rem' : '1rem',
              lineHeight: 1.2
            }}
          >
            {event.title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#666',
              mb: compact ? 0.75 : 1.5,
              fontSize: compact ? '0.7rem' : '0.875rem',
              lineHeight: compact ? 1.3 : 1.5,
              display: compact ? '-webkit-box' : 'block',
              WebkitLineClamp: compact ? 2 : 'unset',
              WebkitBoxOrient: 'vertical',
              overflow: compact ? 'hidden' : 'visible'
            }}
          >
            {event.description}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {displayParticipants.map((p, i) => (
              <Chip
                key={i}
                label={p}
                size="small"
                variant="outlined"
                sx={{ fontSize: compact ? '0.55rem' : '0.65rem', height: compact ? 18 : 22 }}
              />
            ))}
            {hiddenParticipantsCount > 0 && (
              <Typography variant="caption" sx={{ color: '#999', fontSize: '0.55rem' }}>
                +{hiddenParticipantsCount}
              </Typography>
            )}
            {event.mentionedTime && (
              <Chip
                label={compact ? `📅 ${event.mentionedTime.substring(0, 10)}` : `📅 ${event.mentionedTime}`}
                size="small"
                sx={{
                  fontSize: compact ? '0.55rem' : '0.65rem',
                  height: compact ? 18 : 22,
                  bgcolor: '#fff3e0',
                  color: '#e65100'
                }}
              />
            )}
          </Box>
        </Paper>
      </Box>
    </div>
  )
}

// Memoizar EventCard
const EventCard = memo(EventCardBase, (prev, next) => {
  return prev.event.id === next.event.id && prev.isLast === next.isLast && prev.compact === next.compact
})

export default function EventTimelinePanel({ fileId, onNavigateToSegment, speakerMapping = {}, analysisTypeStatus, onAnalysisStarted, analysisOutdated }: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [timeline, setTimeline] = useState<EventTimeline | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [filter, setFilter] = useState<EventCategory | 'all' | 'key'>('all')
  const [timelineFullscreen, setTimelineFullscreen] = useState(false)

  const [reanalyzing, setReanalyzing] = useState(false)

  const handleReanalyze = async () => {
    setReanalyzing(true)
    try {
      await runOnDemandAnalysis(fileId, 'timeline')
      onAnalysisStarted?.()
    } finally {
      setReanalyzing(false)
    }
  }

  const loadTimeline = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getEventTimeline(fileId)
      setTimeline(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar timeline:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (analysisTypeStatus === 'SKIPPED' || analysisTypeStatus === 'PROCESSING') return
    loadTimeline()
  }, [fileId, analysisTypeStatus])

  if (analysisTypeStatus === 'SKIPPED') {
    return (
      <AnalysisSkippedCard
        title="Timeline de Eventos"
        analysisType="timeline"
        fileId={fileId}
        onAnalysisStarted={onAnalysisStarted}
      />
    )
  }

  if (analysisTypeStatus === 'PROCESSING') {
    return <AnalysisProcessingCard title="Timeline de Eventos" />
  }

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
          <CircularProgress size={isMobile ? 36 : 48} />
          <Typography sx={{ mt: 2, color: '#666', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            {isMobile ? 'Extraindo...' : 'Extraindo eventos...'}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
          <Box sx={{ fontSize: { xs: 36, sm: 48 }, mb: { xs: 1.5, sm: 2 } }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            <Button
              variant="outlined"
              onClick={loadTimeline}
              startIcon={<RefreshIcon />}
              size={isMobile ? 'small' : 'medium'}
            >
              {isMobile ? 'Tentar' : 'Tentar novamente'}
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  if (!timeline) {
    return (
      <AnalysisNotAvailableCard 
        title="Timeline de Eventos" 
        onRetry={loadTimeline} 
      />
    )
  }

  const filteredEvents = filter === 'all'
    ? timeline.events
    : filter === 'key'
    ? timeline.keyMoments
    : timeline.eventsByCategory[filter] || []

  const categories = Object.keys(CATEGORY_CONFIG) as EventCategory[]

  return (
    <Box>
      {analysisOutdated && (
        <AnalysisOutdatedBanner
          analysisType="timeline"
          onReanalyze={handleReanalyze}
          isReanalyzing={reanalyzing}
        />
      )}
      {/* Header com estatísticas */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
              <TimelineIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                {isMobile ? 'Timeline' : 'Timeline de Eventos'}
              </Typography>
            </Box>
            <Tooltip title="Atualizar análise" disableHoverListener={isMobile}>
              <IconButton onClick={loadTimeline} size="small">
                <RefreshIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Cards de contagem por categoria */}
          <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            {categories.map((cat) => {
              const config = CATEGORY_CONFIG[cat]
              const Icon = config.icon
              const count = timeline.eventsByCategory[cat]?.length || 0

              return (
                <Grid item xs={4} sm={4} md={2} key={cat}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1, sm: 1.5 },
                      textAlign: 'center',
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      bgcolor: filter === cat ? config.bgColor : '#fff',
                      borderColor: filter === cat ? config.color : '#e0e0e0',
                      transition: 'all 0.2s',
                      borderRadius: { xs: 1, sm: 2 },
                      '&:hover': {
                        bgcolor: config.bgColor,
                        borderColor: config.color
                      }
                    }}
                    onClick={() => setFilter(cat)}
                  >
                    <Icon sx={{ fontSize: { xs: 18, sm: 24 }, color: config.color }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: config.color, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {count}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.55rem', sm: '0.75rem' }, lineHeight: 1.2, display: 'block' }}>
                      {isMobile ? config.label.split('/')[0].substring(0, 8) : config.label}
                    </Typography>
                  </Paper>
                </Grid>
              )
            })}
          </Grid>

          {/* Filtros rápidos - scrollable on mobile */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              overflowX: 'auto',
              mx: { xs: -1, sm: 0 },
              px: { xs: 1, sm: 0 },
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none'
            }}
          >
            <FilterListIcon sx={{ color: '#666', fontSize: { xs: 18, sm: 24 }, flexShrink: 0 }} />
            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} sx={{ minWidth: 'max-content' }}>
              <Chip
                label={isMobile ? `Todos` : `Todos (${timeline.events.length})`}
                onClick={() => setFilter('all')}
                color={filter === 'all' ? 'primary' : 'default'}
                variant={filter === 'all' ? 'filled' : 'outlined'}
                size={isMobile ? 'small' : 'medium'}
                sx={{ fontSize: { xs: '0.65rem', sm: '0.8125rem' } }}
              />
              <Chip
                icon={isMobile ? undefined : <StarIcon />}
                label={isMobile ? `⭐ Chave` : `Momentos-chave (${timeline.keyMoments.length})`}
                onClick={() => setFilter('key')}
                sx={{
                  bgcolor: filter === 'key' ? '#fff3e0' : undefined,
                  borderColor: filter === 'key' ? '#ffc107' : undefined,
                  '& .MuiChip-icon': { color: '#ffc107' },
                  fontSize: { xs: '0.65rem', sm: '0.8125rem' }
                }}
                variant={filter === 'key' ? 'filled' : 'outlined'}
                size={isMobile ? 'small' : 'medium'}
              />
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Timeline - Paginada + Fullscreen */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {filter === 'all'
                ? (isMobile ? `Eventos (${filteredEvents.length})` : `Todos os Eventos (${filteredEvents.length})`)
                : filter === 'key'
                ? (isMobile ? `Chave (${filteredEvents.length})` : `Momentos-chave (${filteredEvents.length})`)
                : `${CATEGORY_CONFIG[filter].label} (${filteredEvents.length})`
              }
            </Typography>
            <Tooltip title="Abrir em tela cheia">
              <IconButton
                size="small"
                onClick={() => setTimelineFullscreen(true)}
                sx={{ color: '#666', '&:hover': { color: '#1976d2' } }}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ pl: { xs: 0, sm: 1 } }}>
            <HybridAnalysisList
              items={filteredEvents}
              renderItem={(event, index) => (
                <EventCard
                  event={event}
                  isLast={index === filteredEvents.length - 1}
                  showConnector={true}
                  onNavigateToSegment={onNavigateToSegment}
                  compact={isMobile}
                  speakerMapping={speakerMapping}
                />
              )}
              emptyMessage={isMobile ? 'Nenhum resultado' : 'Nenhum evento encontrado'}
              emptySubMessage={isMobile ? 'Mude o filtro' : 'Tente selecionar outra categoria'}
              onClearFilters={() => setFilter('all')}
              fullscreenTitle="Timeline de Eventos"
              isFullscreenOpen={timelineFullscreen}
              onFullscreenClose={() => setTimelineFullscreen(false)}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Legenda */}
      <Box sx={{ mt: { xs: 1.5, sm: 2 }, p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1.5 }}>
          {isMobile ? (
            <>
              💡 ⭐ = alta importância. Toque ➔ para ver na transcrição.
            </>
          ) : (
            <>
              💡 <strong>Dica:</strong> A timeline extrai automaticamente eventos mencionados na transcrição.
              Eventos marcados com ⭐ são considerados de alta importância para o contexto.
              Clique em uma categoria para filtrar eventos específicos.
              {onNavigateToSegment && ' Use o botão ➔ para ir diretamente ao segmento na aba Transcrição.'}
            </>
          )}
        </Typography>
      </Box>
    </Box>
  )
}
